import React, { useMemo, useContext } from "react";
import { HtmlContentLoader, IconPosition } from "@shared";
import DynamicHtml from "@common/DynamicHtml";
import CommonService from "@services/CommonService";
import useMediaQuery from "@mui/material/useMediaQuery";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { PAGE, usePageNavigation } from "../contexts/PageNavigationContext";
import { usePageConfiguration } from "../contexts/PageConfigurationContext";
import { SearchContext } from "@contexts/Search";
import Helper from "../Helper";
import { NavigationSection } from "./sections";
import { PendoService } from "@services";

const ChannelHtmlCardPage = () => {
  const { navigateBackward, navigateForward } = usePageNavigation();
  const { getPageConfig, getMobileBreakpoint } = usePageConfiguration();
  const pageConfig = getPageConfig(PAGE.CHANNEL_HTML_CARD);
  const matchesMobileWidth = useMediaQuery(getMobileBreakpoint());
  const { productId, productName, indicationName } = useContext(SearchContext);

  const useHtmlCardPageConfig = (pageConfig) => {
    return useMemo(() => {
      if (!pageConfig?.htmlCardConfig || !pageConfig?.generalSetting) {
        return {
          channel: {
            channelName: "",
            isNoInsurance: false,
          },
          safeChannelName: "",
          htmlDesktopUrl: "",
          htmlMobileUrl: "",
          headerText: "",
          footerText: "",
          backButtonLabel: "Back",
          forwardButtonLabel: "Next",
          backButtonIcon: <ArrowBackIcon />,
          forwardButtonIcon: <ArrowForwardIcon />,
          sideHtmlDesktopUrl: "",
          sideHtmlMobileUrl: "",
          sideHtmlPercentWidth: 0,
          sideHtmlDesktopPosition: "",
          sideHtmlMobilePosition: "",
        };
      }

      const htmlCardConfig = pageConfig?.htmlCardConfig;
      const generalSetting = pageConfig?.generalSetting;

      return {
        channel: pageConfig?.channel,
        safeChannelName: CommonService.getSafeAlphaNumericText(pageConfig?.channel?.channelName),
        htmlDesktopUrl: htmlCardConfig?.htmlFile
          ? CommonService.getHtmlFileUrl(htmlCardConfig.htmlFile, pageConfig.clientId)
          : "",
        htmlMobileUrl: htmlCardConfig?.mobileHtmlFile
          ? CommonService.getHtmlFileUrl(htmlCardConfig.mobileHtmlFile, pageConfig.clientId)
          : "",
        headerText: htmlCardConfig?.header || "",
        footerText: htmlCardConfig?.footer || "",
        backButtonLabel: htmlCardConfig?.backButtonName,
        forwardButtonLabel: htmlCardConfig?.forwardButtonName,
        backButtonIcon: CommonService.getIcon(generalSetting.backButtonIcon, pageConfig.clientId, <ArrowBackIcon />),
        forwardButtonIcon: CommonService.getIcon(generalSetting.forwardButtonIcon, pageConfig.clientId, <ArrowForwardIcon />),
        sideHtmlDesktopUrl: htmlCardConfig?.sideHtmlFile
          ? CommonService.getHtmlFileUrl(htmlCardConfig.sideHtmlFile, pageConfig.clientId)
          : "",
        sideHtmlMobileUrl: htmlCardConfig?.mobileSideHtmlFile
          ? CommonService.getHtmlFileUrl(htmlCardConfig.mobileSideHtmlFile, pageConfig.clientId)
          : "",
        sideHtmlPercentWidth: htmlCardConfig?.sideHtmlScreenSizePercent,
        sideHtmlDesktopPosition: htmlCardConfig?.sideHtmlPosition,
        sideHtmlMobilePosition: htmlCardConfig?.mobileSideHtmlPosition,
      };
    }, [pageConfig]);
  }; 

  const {
    channel,
    safeChannelName,
    htmlDesktopUrl,
    htmlMobileUrl,
    headerText,
    footerText,
    backButtonLabel,
    forwardButtonLabel,
    backButtonIcon,
    forwardButtonIcon,
    sideHtmlDesktopUrl,
    sideHtmlMobileUrl,
    sideHtmlPercentWidth,
    sideHtmlDesktopPosition,
    sideHtmlMobilePosition,
  } = useHtmlCardPageConfig(pageConfig);

  const onAnchorClick = (url) => {
    PendoService.getInstance().trackEvent("PF2 Custom Link Click", "ChannelHtmlCardPage > " + safeChannelName, url, productId, productName, indicationName);
  };

  const sideHtml = useMemo(() => {
    const config = {
      sideHtmlDesktopUrl,
      sideHtmlMobileUrl,
      sideHtmlDesktopPosition,
      sideHtmlMobilePosition,
      sideHtmlPercentWidth
    };
    return Helper.getSideHtmlConfig(config, matchesMobileWidth);
  }, [sideHtmlDesktopUrl, sideHtmlMobileUrl, sideHtmlDesktopPosition, sideHtmlMobilePosition, sideHtmlPercentWidth, matchesMobileWidth]);

  const containerClassName = `norstella-pf-htmlCardPage-container ${sideHtml.containerClass} norstella-pf-${safeChannelName}-htmlCardPage-container`;

  return (
    <div className={`norstella-pf-channelCardPageContainer norstella-pf-${safeChannelName}-channelCardPageContainer`}>      
      {/* Side HTML Panel */}
      {sideHtml.showHtml && (
        <div className={`norstella-pf-SideHtmlLeftTop norstella-pf-${safeChannelName}-SideHtmlLeftTop`} style={{ width: sideHtml.width,order: sideHtml.htmlOrder}}>
          <HtmlContentLoader url={sideHtml.htmlUrl} onAnchorClick={onAnchorClick} />
        </div>
      )}
      
      {/* Main Content Container */}
      <div className={containerClassName} style={{ width: sideHtml.contentWidth, order: sideHtml.contentOrder }}>
        {/* Navigation Section */}
        <NavigationSection
          backButtonLabel={backButtonLabel}
          backButtonIcon={backButtonIcon}
          navigateBackward={navigateBackward}
          forwardButtonLabel={forwardButtonLabel}
          forwardButtonIcon={forwardButtonIcon}
          forwardButtonIconPosition={IconPosition.RIGHT}
          navigateForward={navigateForward}
          headerText={headerText}
          safeChannelName={safeChannelName}
          channel={channel}
          pageName={"htmlCard"}
        />

        {/* Dynamic HTML Content */}
        {(htmlDesktopUrl || htmlMobileUrl) && (
          <div className={`norstella-pf-content-section norstella-pf-${safeChannelName}-content-section`}>
            <HtmlContentLoader url={matchesMobileWidth ? htmlMobileUrl : htmlDesktopUrl} onAnchorClick={onAnchorClick} />
          </div>
        )}

        {/* Footer */}
        {footerText && footerText.trim() !== "" && (
          <div className={`norstella-pf-footer-section norstella-pf-${safeChannelName}-footer-section`}>
            <DynamicHtml input={footerText} className="norstella-pf-footer left-aligned" part="pfDivFooterText" channelName={channel.channelName} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelHtmlCardPage;
